import { useLayoutEffect, useState } from "react";
import { Autocomplete, AutocompleteRenderInputParams, Chip, TextField } from "@mui/material";
import { calculateTagLimit } from "../../../components/BookingForm/BookingFilter/functions/calculateTagLimit.function";
import { TeamMember } from "../../Booking-Form/typings/team-member";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import { getOptionLabel, getOptionLabelWithIndex } from "../schedule.functions";
import { useTranslation } from "react-i18next";
import { ColleagueSelectFieldEndIcons } from "./ScheduleColleagueSelectFieldEndIcons.partial";

const colleagueChipEndGap = 8;
const colleagueChipEndFallback = 96;

function colleagueRows(selectedColleagues: TeamMember[]): ConnectedBookingData[] {
  return selectedColleagues.map(colleague => ({
    firstName: colleague.firstName ?? "",
    lastName: colleague.surname ?? "",
    userId: colleague.userId ?? "",
    email: colleague.email ?? "",
    bookingInventoryId: colleague.bookingInventoryId ?? 0
  }));
}

function endAdornmentPadding(root: HTMLDivElement): number {
  const adornment = root.querySelector(
    "[data-testid='choose-colleagues-textField-input-adornment']"
  );
  if (adornment instanceof HTMLElement && adornment.offsetWidth > 0) {
    return adornment.offsetWidth + colleagueChipEndGap;
  }
  return colleagueChipEndFallback;
}

type Props = {
  containerRef: React.RefObject<HTMLDivElement>;
  tagLimit: number;
  colleagues: TeamMember[];
  selectedColleagues: TeamMember[];
  scheduleColleaguesIsFetching: boolean;
  storedScheduleColleaguesIsFetching: boolean;
  preStoredColleagues: string[];
  isFirstAidSelected: boolean;
  setSelectedColleagues: (col: TeamMember[]) => void;
  handleStoredScheduleColleagues: () => void;
};

export function ScheduleColleagueSelect({
  containerRef,
  tagLimit,
  colleagues,
  selectedColleagues,
  scheduleColleaguesIsFetching,
  storedScheduleColleaguesIsFetching,
  preStoredColleagues,
  isFirstAidSelected,
  setSelectedColleagues,
  handleStoredScheduleColleagues
}: Readonly<Props>) {
  const { t } = useTranslation();
  const [measuredTagLimit, setMeasuredTagLimit] = useState<number | null>(null);
  const visibleTagLimit = measuredTagLimit ?? tagLimit;

  useLayoutEffect(() => {
    const applyLimit = () => {
      const root = containerRef.current;
      if (!root) {
        return;
      }
      calculateTagLimit(
        containerRef,
        endAdornmentPadding(root),
        colleagueRows(selectedColleagues),
        setMeasuredTagLimit
      );
    };

    applyLimit();
    if (typeof ResizeObserver === "undefined" || !containerRef.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(applyLimit);
    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, selectedColleagues]);

  return (
    <Autocomplete
      ref={containerRef}
      data-testid="schedule-team-selection"
      multiple
      id="tags-outlined4"
      limitTags={visibleTagLimit}
      disableCloseOnSelect
      options={colleagues}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.userId}>
            {getOptionLabelWithIndex(option)}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            label={getOptionLabelWithIndex(option)}
            key={option.userId}
          />
        ));
      }}
      sx={{
        flex: "1 1 280px",
        minWidth: { xs: "100%", sm: 280 },
        maxWidth: "100%",
        "& .MuiOutlinedInput-root": {
          borderRadius: 1.5
        }
      }}
      onChange={(_event, values) => {
        setSelectedColleagues(values);
      }}
      isOptionEqualToValue={(option, value) => option.userId === value.userId}
      value={selectedColleagues}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          size="small"
          fullWidth
          placeholder={t("Choose your colleagues")}
          slotProps={{
            input: {
              ...params.InputProps,
              sx: { pr: "4px !important", height: "50px" },
              endAdornment: (
                <ColleagueSelectFieldEndIcons
                  handleStoreColleagues={handleStoredScheduleColleagues}
                  isLoading={scheduleColleaguesIsFetching || storedScheduleColleaguesIsFetching}
                  isPreStored={selectedColleagues.some(col =>
                    preStoredColleagues.includes(col.userId)
                  )}
                  disabled={isFirstAidSelected}
                  onClear={() => setSelectedColleagues([])}
                />
              )
            }
          }}
        />
      )}
    />
  );
}
